import React, { useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { Modal, Button } from "react-bootstrap";
import {
  ArrowLeft,
  Calendar,
  Info,
  Presentation,
  Images,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  FileText,
  ImageIcon,
  Clock,
  MapPin,
  Youtube,
} from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";

const EventView = ({ event, isLoading, isError, error, formattedTitle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const galleryImages = event?.galleryImages || [];
  const presentations = event?.presentations || [];
  const resources = event?.resources || [];

  function openPopus(index) {
    setSelectedIndex(index);
    setIsOpen(true);
  }

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const nextImage = () => {
    setSelectedIndex((nextIndex) => (nextIndex + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setSelectedIndex((prevIndex) =>
      galleryImages.length > 0
        ? (prevIndex - 1 + galleryImages.length) % galleryImages.length
        : 0,
    );
  };

  const formatEventDate = (dateString) => {
    if (!dateString) return "Date not available";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Helper function to convert any YouTube link into an iframe embed link
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return "";
    let videoId = "";

    if (url.includes("shorts/")) {
      videoId = url.split("shorts/")[1].split("?")[0];
    } else if (url.includes("watch?v=")) {
      videoId = url.split("watch?v=")[1].split("&")[0];
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1].split("?")[0];
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  // Dynamically generate tabs to easily manage the activeTabIndex
  const availableTabs = [{ id: "about", title: "About Event", icon: Info }];
  if (presentations.length > 0) {
    availableTabs.push({
      id: "presentations",
      title: `Presentations (${presentations.length})`,
      icon: Presentation,
    });
  }
  if (galleryImages.length > 0) {
    availableTabs.push({
      id: "gallery",
      title: `Gallery (${galleryImages.length})`,
      icon: Images,
    });
  }
  if (resources.length > 0) {
    availableTabs.push({
      id: "videos",
      title: `Videos (${resources.length})`,
      icon: Youtube,
    });
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div
            className="spinner-border text-primary mb-3"
            role="status"
            style={{ color: "#4F22D8 !important" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div
            className="alert alert-danger d-inline-flex align-items-center"
            role="alert"
          >
            <X className="me-2" size={20} />
            {error.message}
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <Info className="mb-3 text-muted" size={48} />
          <p className="text-muted">No event details available.</p>
        </div>
      </div>
    );
  }

  return (
    <main>
      <div className="postbox__area fix pt-50 pb-0">
        <div className="container">
          <div className="row">
            <div className="col-xxl-12 min-h-screen">
              <div className="postbox__details-wrapper">
                <article>
                  <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div className="card-body p-4 p-md-5">
                      <div className="row g-4">
                        {event.mainImage && (
                          <div className="col-lg-6">
                            <div className="text-center mb-3">
                              <h4
                                className="fw-bold mb-3"
                                style={{ color: "#2c3e50" }}
                              >
                                <ImageIcon
                                  size={24}
                                  className="me-2"
                                  style={{ color: "#4F22D8" }}
                                />
                                Main Event Image
                              </h4>
                            </div>
                            <div
                              className="position-relative overflow-hidden rounded-4 shadow-sm"
                              style={{ height: "350px" }}
                            >
                              <img
                                src={event.mainImage}
                                alt="Main Event Image"
                                className="w-100 h-100"
                                style={{
                                  objectFit: "cover",
                                  transition: "transform 0.3s ease",
                                }}
                                onError={(e) => {
                                  e.target.src = "/placeholder-image.jpg";
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = "scale(1.05)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = "scale(1)";
                                }}
                              />
                              <div
                                className="position-absolute top-0 start-0 w-100 h-100"
                                style={{
                                  background:
                                    "linear-gradient(45deg, rgba(79, 34, 216, 0.1), rgba(0, 0, 0, 0.1))",
                                }}
                              ></div>
                            </div>
                          </div>
                        )}

                        {event.image && (
                          <div className="col-lg-6">
                            <div className="text-center mb-3">
                              <h4
                                className="fw-bold mb-3"
                                style={{ color: "#2c3e50" }}
                              >
                                <ImageIcon
                                  size={24}
                                  className="me-2"
                                  style={{ color: "#4F22D8" }}
                                />
                                Title Image
                              </h4>
                            </div>
                            <div
                              className="position-relative overflow-hidden rounded-4 shadow-sm"
                              style={{ height: "350px" }}
                            >
                              <img
                                src={event.image}
                                alt="Title Image"
                                className="w-100 h-100"
                                style={{
                                  objectFit: "cover",
                                  transition: "transform 0.3s ease",
                                }}
                                onError={(e) => {
                                  e.target.src = "/placeholder-image.jpg";
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = "scale(1.05)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = "scale(1)";
                                }}
                              />
                              <div
                                className="position-absolute top-0 start-0 w-100 h-100"
                                style={{
                                  background:
                                    "linear-gradient(45deg, rgba(79, 34, 216, 0.1), rgba(0, 0, 0, 0.1))",
                                }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="row mt-4">
                        <div className="col-12">
                          <div className="d-flex flex-wrap justify-content-center gap-4 gap-md-5">
                            {presentations.length > 0 && (
                              <div className="text-center">
                                <div
                                  className="fw-bold mb-1"
                                  style={{
                                    color: "#4F22D8",
                                    fontSize: "2rem",
                                  }}
                                >
                                  {presentations.length}
                                </div>
                                <small className="text-muted fw-medium">
                                  Presentations Available
                                </small>
                              </div>
                            )}
                            {galleryImages.length > 0 && (
                              <div className="text-center">
                                <div
                                  className="fw-bold mb-1"
                                  style={{
                                    color: "#4F22D8",
                                    fontSize: "2rem",
                                  }}
                                >
                                  {galleryImages.length}
                                </div>
                                <small className="text-muted fw-medium">
                                  Gallery Images
                                </small>
                              </div>
                            )}
                            {resources.length > 0 && (
                              <div className="text-center">
                                <div
                                  className="fw-bold mb-1"
                                  style={{
                                    color: "#4F22D8",
                                    fontSize: "2rem",
                                  }}
                                >
                                  {resources.length}
                                </div>
                                <small className="text-muted fw-medium">
                                  Event Videos
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div className="card-body p-4 p-md-5">
                      <Tabs
                        className="it-course-details-wrap mr-0"
                        selectedIndex={activeTabIndex}
                        onSelect={(index) => setActiveTabIndex(index)}
                      >
                        <div className="it-course-details-nav pb-3 pb-md-4">
                          <nav>
                            <TabList
                              className="nav nav-pills d-flex flex-wrap gap-2 border-0 bg-transparent p-0"
                              id="nav-tab"
                              role="tablist"
                            >
                              {availableTabs.map((tab, index) => {
                                const IconComponent = tab.icon;
                                const isActive = activeTabIndex === index;
                                return (
                                  <Tab className="nav-item" key={tab.id}>
                                    <button
                                      className={`nav-link d-flex align-items-center px-4 py-2 border-0 rounded-3 fw-medium transition-all ${
                                        isActive ? "active" : ""
                                      }`}
                                      style={{
                                        backgroundColor: isActive
                                          ? "#4F22D8"
                                          : "transparent",
                                        color: isActive ? "#ffffff" : "#6c757d",
                                        transition: "all 0.3s ease",
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isActive) {
                                          e.target.style.backgroundColor =
                                            "#f1f3f4";
                                          e.target.style.color = "#4F22D8";
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isActive) {
                                          e.target.style.backgroundColor =
                                            "transparent";
                                          e.target.style.color = "#6c757d";
                                        }
                                      }}
                                    >
                                      <IconComponent
                                        size={18}
                                        className="me-2"
                                      />
                                      {tab.title}
                                    </button>
                                  </Tab>
                                );
                              })}
                            </TabList>
                          </nav>
                        </div>

                        <div className="it-course-details-content">
                          <div className="tab-content" id="nav-tabContent">
                            {/* ABOUT TAB PANEL */}
                            <TabPanel>
                              <div className="row">
                                <div className="col-xxl-12">
                                  <div
                                    className="inner-content-card border-0 rounded-3"
                                    style={{ backgroundColor: "#f8f9fa" }}
                                  >
                                    <div className="card-body p-4">
                                      <div className="d-flex align-items-center mb-4">
                                        <div
                                          className="d-flex align-items-center justify-content-center rounded-3 me-3"
                                          style={{
                                            width: "48px",
                                            height: "48px",
                                            backgroundColor: "#4F22D8",
                                            color: "white",
                                          }}
                                        >
                                          <FileText size={24} />
                                        </div>
                                        <h4
                                          className="mb-0 fw-bold"
                                          style={{ color: "#2c3e50" }}
                                        >
                                          Event Description
                                        </h4>
                                      </div>

                                      {event?.description ? (
                                        <div
                                          className="event-description-content"
                                          style={{
                                            lineHeight: "1.7",
                                            fontSize: "1.05rem",
                                            color: "#4a5568",
                                          }}
                                          dangerouslySetInnerHTML={{
                                            __html: event.description,
                                          }}
                                        />
                                      ) : (
                                        <div className="text-center py-5">
                                          <Info
                                            size={48}
                                            className="text-muted mb-3"
                                          />
                                          <p className="text-muted mb-0 fs-5">
                                            No description available for this
                                            event.
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </TabPanel>

                            {/* PRESENTATIONS TAB PANEL */}
                            {presentations.length > 0 && (
                              <TabPanel>
                                <div
                                  className="inner-content-card border-0 rounded-3"
                                  style={{ backgroundColor: "#f8f9fa" }}
                                >
                                  <div className="card-header bg-transparent border-0 p-4 pb-0">
                                    <div className="d-flex align-items-center">
                                      <div
                                        className="d-flex align-items-center justify-content-center rounded-3 me-3"
                                        style={{
                                          width: "48px",
                                          height: "48px",
                                          backgroundColor: "#4F22D8",
                                          color: "white",
                                        }}
                                      >
                                        <Presentation size={24} />
                                      </div>
                                      <h4
                                        className="mb-0 fw-bold"
                                        style={{ color: "#2c3e50" }}
                                      >
                                        Event Presentations
                                      </h4>
                                    </div>
                                  </div>
                                  <div className="card-body p-4">
                                    <div className="table-responsive">
                                      <table className="table table-hover">
                                        <thead
                                          style={{ backgroundColor: "#ffffff" }}
                                        >
                                          <tr>
                                            <th
                                              className="border-0 py-3 fw-semibold rounded-start"
                                              style={{ color: "#4F22D8" }}
                                            >
                                              Presentation Title
                                            </th>
                                            <th
                                              className="text-center border-0 py-3 fw-semibold rounded-end"
                                              style={{ color: "#4F22D8" }}
                                            >
                                              Download
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {presentations.map(
                                            (presentation, idx) => (
                                              <tr
                                                key={idx}
                                                style={{
                                                  transition: "all 0.2s ease",
                                                }}
                                              >
                                                <td className="py-3 align-middle">
                                                  <div className="d-flex align-items-center">
                                                    <div
                                                      className="d-flex align-items-center justify-content-center rounded-2 me-3"
                                                      style={{
                                                        width: "32px",
                                                        height: "32px",
                                                        backgroundColor:
                                                          "#e8f2ff",
                                                        color: "#4F22D8",
                                                      }}
                                                    >
                                                      <FileText size={16} />
                                                    </div>
                                                    <span
                                                      className="fw-medium"
                                                      style={{
                                                        color: "#2c3e50",
                                                      }}
                                                    >
                                                      {presentation.title}
                                                    </span>
                                                  </div>
                                                </td>
                                                <td className="text-center py-3 align-middle">
                                                  <a
                                                    href={presentation.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    download
                                                    className="btn d-inline-flex align-items-center px-3 py-2 text-white text-decoration-none rounded-3 shadow-sm"
                                                    style={{
                                                      backgroundColor:
                                                        "#4F22D8",
                                                      fontSize: "0.9rem",
                                                      transition:
                                                        "all 0.3s ease",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      e.target.style.backgroundColor =
                                                        "#3d1aa6";
                                                      e.target.style.transform =
                                                        "translateY(-1px)";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                      e.target.style.backgroundColor =
                                                        "#4F22D8";
                                                      e.target.style.transform =
                                                        "translateY(0)";
                                                    }}
                                                  >
                                                    <Download
                                                      size={14}
                                                      className="me-1"
                                                    />
                                                    Download
                                                  </a>
                                                </td>
                                              </tr>
                                            ),
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </TabPanel>
                            )}

                            {/* GALLERY TAB PANEL */}
                            {galleryImages.length > 0 && (
                              <TabPanel>
                                <div
                                  className="inner-content-card border-0 rounded-3"
                                  style={{ backgroundColor: "#f8f9fa" }}
                                >
                                  <div className="card-header bg-transparent border-0 p-4 pb-0">
                                    <div className="d-flex align-items-center">
                                      <div
                                        className="d-flex align-items-center justify-content-center rounded-3 me-3"
                                        style={{
                                          width: "48px",
                                          height: "48px",
                                          backgroundColor: "#4F22D8",
                                          color: "white",
                                        }}
                                      >
                                        <ImageIcon size={24} />
                                      </div>
                                      <h4
                                        className="mb-0 fw-bold"
                                        style={{ color: "#2c3e50" }}
                                      >
                                        Event Gallery
                                      </h4>
                                    </div>
                                  </div>
                                  <div className="card-body p-4">
                                    <div className="row g-4">
                                      {galleryImages.map((image, index) => (
                                        <div
                                          className="col-xxl-3 col-xl-4 col-lg-4 col-md-6"
                                          key={index}
                                        >
                                          <div
                                            className="position-relative overflow-hidden rounded-4 shadow-sm"
                                            style={{
                                              cursor: "pointer",
                                              transition: "all 0.3s ease",
                                            }}
                                            onClick={() => openPopus(index)}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.transform =
                                                "translateY(-4px)";
                                              e.currentTarget.style.boxShadow =
                                                "0 8px 25px rgba(79, 34, 216, 0.15)";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.transform =
                                                "translateY(0)";
                                              e.currentTarget.style.boxShadow =
                                                "0 2px 10px rgba(0, 0, 0, 0.1)";
                                            }}
                                          >
                                            <img
                                              src={image}
                                              alt={`Event image ${index + 1}`}
                                              style={{
                                                width: "100%",
                                                height: "240px",
                                                objectFit: "cover",
                                              }}
                                            />
                                            <div
                                              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center opacity-0"
                                              style={{
                                                backgroundColor:
                                                  "rgba(79, 34, 216, 0.7)",
                                                transition: "opacity 0.3s ease",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.target.style.opacity = "1";
                                              }}
                                            >
                                              <ImageIcon
                                                size={32}
                                                className="text-white"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>

                                <Modal
                                  show={isOpen}
                                  onHide={toggleModal}
                                  centered
                                  size="xl"
                                  className="image-modal"
                                >
                                  <Modal.Body className="p-0 position-relative">
                                    <Button
                                      variant="light"
                                      onClick={toggleModal}
                                      className="position-absolute d-flex align-items-center justify-content-center"
                                      style={{
                                        top: "20px",
                                        right: "20px",
                                        zIndex: 1050,
                                        borderRadius: "50%",
                                        width: "40px",
                                        height: "40px",
                                        backgroundColor:
                                          "rgba(255, 255, 255, 0.9)",
                                        border: "none",
                                        boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
                                      }}
                                    >
                                      <X
                                        size={20}
                                        style={{ color: "#2c3e50" }}
                                      />
                                    </Button>

                                    {galleryImages.length > 1 && (
                                      <>
                                        <Button
                                          variant="light"
                                          onClick={prevImage}
                                          className="position-absolute d-flex align-items-center justify-content-center"
                                          style={{
                                            top: "50%",
                                            left: "20px",
                                            transform: "translateY(-50%)",
                                            zIndex: 1050,
                                            borderRadius: "50%",
                                            width: "50px",
                                            height: "50px",
                                            backgroundColor:
                                              "rgba(255, 255, 255, 0.9)",
                                            border: "none",
                                            boxShadow:
                                              "0 2px 10px rgba(0,0,0,0.3)",
                                          }}
                                        >
                                          <ChevronLeft
                                            size={24}
                                            style={{ color: "#2c3e50" }}
                                          />
                                        </Button>

                                        <Button
                                          variant="light"
                                          onClick={nextImage}
                                          className="position-absolute d-flex align-items-center justify-content-center"
                                          style={{
                                            top: "50%",
                                            right: "20px",
                                            transform: "translateY(-50%)",
                                            zIndex: 1050,
                                            borderRadius: "50%",
                                            width: "50px",
                                            height: "50px",
                                            backgroundColor:
                                              "rgba(255, 255, 255, 0.9)",
                                            border: "none",
                                            boxShadow:
                                              "0 2px 10px rgba(0,0,0,0.3)",
                                          }}
                                        >
                                          <ChevronRight
                                            size={24}
                                            style={{ color: "#2c3e50" }}
                                          />
                                        </Button>
                                      </>
                                    )}

                                    {galleryImages.length > 0 && (
                                      <img
                                        src={galleryImages[selectedIndex]}
                                        alt="Full size"
                                        style={{
                                          width: "100%",
                                          height: "80vh",
                                          objectFit: "cover",
                                          display: "block",
                                        }}
                                      />
                                    )}

                                    <div
                                      className="position-absolute bottom-0 start-50 translate-middle-x mb-3 px-3 py-2 rounded-3 text-white fw-medium"
                                      style={{
                                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                                        fontSize: "0.9rem",
                                      }}
                                    >
                                      {selectedIndex + 1} /{" "}
                                      {galleryImages.length}
                                    </div>
                                  </Modal.Body>
                                </Modal>
                              </TabPanel>
                            )}

                            {/* VIDEOS / RESOURCES TAB PANEL */}
                            {resources.length > 0 && (
                              <TabPanel>
                                <div
                                  className="inner-content-card border-0 rounded-3"
                                  style={{ backgroundColor: "#f8f9fa" }}
                                >
                                  <div className="card-header bg-transparent border-0 p-4 pb-0">
                                    <div className="d-flex align-items-center mb-2">
                                      <div
                                        className="d-flex align-items-center justify-content-center rounded-3 me-3"
                                        style={{
                                          width: "48px",
                                          height: "48px",
                                          backgroundColor: "#4F22D8",
                                          color: "white",
                                        }}
                                      >
                                        <Youtube size={24} />
                                      </div>
                                      <h4
                                        className="mb-0 fw-bold"
                                        style={{ color: "#2c3e50" }}
                                      >
                                        Event Videos
                                      </h4>
                                    </div>
                                  </div>

                                  <div className="card-body p-4">
                                    <div className="row g-4">
                                      {resources.map((resource, index) => (
                                        <div className="col-lg-6" key={index}>
                                          <div
                                            className="rounded-4 overflow-hidden shadow-sm bg-white d-flex flex-column"
                                            style={{
                                              border: "1px solid #eaeaea",
                                              height: "100%",
                                            }}
                                          >
                                            <div className="p-3 border-bottom">
                                              <h6
                                                className="mb-0 fw-bold text-dark text-truncate"
                                                title={resource.label}
                                              >
                                                {resource.label}
                                              </h6>
                                            </div>
                                            {/* Responsive Iframe Container */}
                                            <div className="ratio ratio-16x9 flex-grow-1 bg-dark">
                                              <iframe
                                                className="position-absolute top-0 start-0 w-100 h-100 border-0"
                                                src={getYouTubeEmbedUrl(
                                                  resource.link,
                                                )}
                                                title={resource.label}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                referrerPolicy="strict-origin-when-cross-origin"
                                                allowFullScreen
                                              ></iframe>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </TabPanel>
                            )}
                          </div>
                        </div>
                      </Tabs>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .nav-pills .nav-link.active {
          background-color: #4f22d8 !important;
          color: white !important;
          box-shadow: 0 2px 8px rgba(79, 34, 216, 0.3);
        }

        .nav-pills .nav-link:hover {
          background-color: rgba(79, 34, 216, 0.1) !important;
          color: #4f22d8 !important;
        }

        .table tbody tr:hover {
          background-color: rgba(79, 34, 216, 0.05) !important;
        }

        .spinner-border {
          color: #4f22d8 !important;
          width: 2rem;
          height: 2rem;
        }

        .image-modal .modal-dialog {
          max-width: 50vw !important;
        }

        .image-modal .modal-content {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }

        .inner-content-card {
          background-color: #f8f9fa;
        }
      `}</style>
    </main>
  );
};

export default EventView;
